import { buildDataTableCols, type DataTableColumn, type DataTableRenderCellParams } from '@/components/custom/erp';
import { formatDate, formatMoney, formatNumber, formatRoundedNumber } from '@/lib/formatUtils';

export const getDataTableColumns = (handleOpenViewForm: (row: BanHangRecord) => void): DataTableColumn[] =>
  buildDataTableCols<BanHangRecord>([
    {
      field: 'status',
      headerName: 'Trạng thái',
      width: 120,
      renderCell: (params: DataTableRenderCellParams) => {
        switch (params.row.status) {
          case '0':
            return 'Chưa ghi sổ';
          case '3':
            return 'Chờ duyệt';
          case '5':
            return 'Đã ghi sổ';
          case '9':
            return 'Hủy';
        }
      }
    },
    {
      field: 'ma_tthddt',
      headerName: 'Trạng thái HĐĐT',
      width: 150,
      renderCell: (params: DataTableRenderCellParams) => {
        switch (params.row.ma_tthddt) {
          case '0':
            return 'Không sử dụng';
          case '1':
            return 'Chờ phát hành';
          case '2':
            return 'Chờ ký';
          case '3':
            return 'Xác thực';
          case '4':
            return 'Điều chỉnh';
          case '5':
            return 'Thay thế';
          case '7':
            return 'Thuế từ chối';
          case '8':
            return 'Chờ hủy';
          case '9':
            return 'Hủy';
          case '10':
            return 'Thuế không đồng ý';
        }
      }
    },
    {
      field: 'so_ct',
      headerName: 'Số c/từ',
      width: 140,
      renderCell: (params: DataTableRenderCellParams) => (
        <div
          className='cursor-pointer hover:text-main hover:underline'
          onClick={e => {
            e.stopPropagation();
            handleOpenViewForm(params.row);
          }}
        >
          {params.row.so_ct}
        </div>
      )
    },
    {
      field: 'ngay_ct',
      headerName: 'Ngày c/từ',
      width: 120,
      renderCell: (params: DataTableRenderCellParams) => formatDate(params.row.ngay_ct)
    },
    {
      field: 'ma_kh',
      headerName: 'Mã khách hàng',
      width: 150,
      renderCell: (params: DataTableRenderCellParams) => {
        return params.row.ma_kh_data?.customer_code;
      }
    },
    {
      field: 'ten_kh',
      headerName: 'Tên khách hàng',
      width: 200,
      renderCell: (params: DataTableRenderCellParams) => {
        return params.row.ma_kh_data?.customer_name;
      }
    },
    {
      field: 'tk',
      headerName: 'Tài khoản nợ',
      width: 120,
      renderCell: (params: DataTableRenderCellParams) => {
        return params.row.tk_data?.code;
      }
    },
    {
      field: 't_tt_nt',
      headerName: 'Thanh toán',
      width: 170,
      columnType: 'number',
      renderCell: params => formatRoundedNumber(params.row.t_tt_nt)
    },
    {
      field: 'ma_nt',
      headerName: 'Ngoại tệ',
      width: 100,
      renderCell: (params: DataTableRenderCellParams) => params.row.ma_nt_data?.ma_nt
    }
  ]);

export const getDetailDataTableColumns: DataTableColumn[] = buildDataTableCols<BanHangRecord>([
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_vt_data?.ma_vt
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: params => params.row.ma_vt_data?.ten_vt
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 80,
    renderCell: params => params.row.dvt_data?.ten_dvt
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 120,
    renderCell: params => params.row.ma_kho_data?.ma_kho
  },
  {
    field: 'ma_vi_tri',
    headerName: 'Mã vị trí',
    width: 120,
    renderCell: params => params.row.ma_vi_tri_data?.ma_vi_tri
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 100,
    renderCell: params => formatNumber(params.row.so_luong)
  },
  {
    field: 'gia_nt2',
    headerName: 'Giá bán VND',
    width: 120,
    renderCell: params => formatMoney(params.row.gia_nt2)
  },
  {
    field: 'tien_nt2',
    headerName: 'Thành tiền VND',
    width: 120,
    renderCell: params => formatMoney(params.row.tien_nt2)
  },
  { field: 'tl_ck', headerName: 'Tl ck(%)', width: 100, columnType: 'number' },
  {
    field: 'ck_nt',
    headerName: 'Ch.khấu VND',
    width: 120,
    renderCell: params => formatMoney(params.row.ck_nt)
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 150,
    renderCell: params => params.row.ma_thue_data?.ten_thue
  },
  {
    field: 'tk_thue_no',
    headerName: 'Tk thuế nợ',
    width: 120,
    renderCell: params => params.row.tk_thue_no_data?.code
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế VND',
    width: 120,
    renderCell: params => formatMoney(params.row.thue_nt)
  },
  {
    field: 'tk_du',
    headerName: 'Tk đối ứng',
    width: 120,
    renderCell: params => params.row.tk_du_data?.code
  },
  {
    field: 'tk_ck',
    headerName: 'Tk chiết khấu',
    width: 120,
    renderCell: params => params.row.tk_ck_data?.code
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 100,
    renderCell: params => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 100,
    renderCell: params => params.row.ma_vv_data?.ma_vv
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 100,
    renderCell: params => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: params => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 100,
    renderCell: params => params.row.ma_ku_data?.ma_ku
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 100,
    renderCell: params => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 100,
    renderCell: params => params.row.ma_sp_data?.ma_vt
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 150,
    renderCell: params => params.row.ma_lsx_data?.ma_lsx
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: params => params.row.ma_cp0_data?.ma_cpkhl
  }
]);

export const historyColumns: DataTableColumn[] = buildDataTableCols<BanHangRecord>([
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
]);
